import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Check, Flower2 } from 'lucide-react';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { useSalvaCampo, conInvio } from '../../utils/salvaCampo';
import StatoSalvataggio from '../StatoSalvataggio';
import { anamnesiFisiologicaAPI } from '../../services/api';
import { messaggioDa } from '../../utils/notifiche';
import '../../styles/SchedaFisiologica.css';
import '../../styles/SchedaFisiologicaConsole.css';

/**
 * RiquadroGravidanze — gravidanze, climaterio e menopausa.
 *
 * UN SOLO DATO, SCRIVIBILE DA DUE POSTI (decisione di Alex, 23 settembre
 * 2026). Il dato vive solo nell'anamnesi (anamnesi_fisiologica_dettaglio):
 * nessuna copia, nessuna sincronizzazione. Questo componente - lo stesso,
 * con la stessa rotta e le stesse regole - compare nell'anamnesi generale,
 * sotto «Parto», e nella scheda ginecologica, dopo «Storia ostetrica».
 * Quello che la ginecologa scrive nella scheda e' gia' nell'anamnesi, e
 * viceversa. `origine` dice da quale dei due posti si scrive: la rotta lo
 * dichiara al database, e il trigger dell'ultima modifica lo scrive accanto
 * all'autore, che si legge in testa al riquadro.
 *
 * LE REGOLE, uguali ovunque, e le fa rispettare anche il server (e il
 * database): solo per le pazienti; il «no» chiude il campo che ne dipende
 * e, se c'era scritto qualcosa, lo cancella dopo una conferma; l'eta'
 * della menopausa fra 10 e 70 (plausibilita', non valori clinici); si
 * salva uscendo dal campo; tutto nel registro delle modifiche con l'autore.
 * Il numero dei parti non si scrive: si conta dalle righe dell'elenco.
 *
 * LE NOTE (migration 343, Alex 26/10/2026): una per voce - Gravidanze,
 * Climaterio, Menopausa - facoltativa, su piu' righe, sotto i Si'/No di
 * quella voce. Sono colonne della stessa riga, quindi lo stesso dato dai due
 * posti; si salvano uscendo dal campo come gli altri (e con Invio;
 * Maiusc+Invio va a capo), e non dipendono dal si/no: un «No» non le
 * cancella. Nella sola lettura di un giorno passato si leggono com'erano.
 *
 * Il riquadro carica e salva da se': dentro la scheda ginecologica non
 * passa dal «Salva la scheda», e nell'anamnesi la barra di salvataggio
 * del resto della scheda fisiologica non lo tocca.
 *
 * DENTRO UNA SCHEDA E' IL RIQUADRO DEL GIORNO DELLA VISITA (Alex, 23
 * settembre 2026): la compilazione di oggi lo mostra modificabile, col
 * dato vivo; una compilazione di un giorno passato lo mostra COM'ERA quel
 * giorno, in sola lettura, con «Valore al … — per modificarlo apri
 * l'anamnesi». Il giorno, il momento e il valore li decide il server
 * (rotta gravidanze-al, services/gravidanzeAl.js): la stessa fonte della
 * stampa della scheda e del referto, perche' schermata e referto della
 * stessa visita dicano la stessa cosa.
 */

const SI_NO = [
    { valore: true, etichetta: 'Sì' },
    { valore: false, etichetta: 'No' },
];
const DIPENDE_DA = { gravidanze: 'gravidanze_elenco', climaterio: 'climaterio_sintomi', menopausa: 'menopausa_eta' };
const CONFERMA = {
    gravidanze: 'L\'elenco delle gravidanze si cancella. Procedo? Quello che c\'era resta nel registro delle modifiche.',
    climaterio: 'I sintomi del climaterio si cancellano. Procedo? Quello che c\'era resta nel registro delle modifiche.',
    menopausa: 'L\'età della menopausa si cancella. Procedo? Quello che c\'era resta nel registro delle modifiche.',
};
const DOVE = { anamnesi: 'dall\'anamnesi', scheda_ginecologica: 'dalla scheda ginecologica' };

/* I campi che sono di questo riquadro, compresa l'ultima modifica (che
   scrive il database). Chi legge la stessa riga per altri motivi - la
   scheda fisiologica - li toglie dai suoi dati, per non riscriverli con
   un valore vecchio quando salva in blocco. */
export const CAMPI_DEL_RIQUADRO = [
    'gravidanze', 'gravidanze_elenco', 'climaterio', 'climaterio_sintomi', 'menopausa', 'menopausa_eta',
    'gravidanze_aggiornato_il', 'gravidanze_aggiornato_da', 'gravidanze_aggiornato_nome', 'gravidanze_aggiornato_dove',
    'gravidanze_note', 'climaterio_note', 'menopausa_note',
];

/* La nota di ogni voce (343): il campo e come si chiama a schermo. */
const NOTA_DI = {
    gravidanze: { campo: 'gravidanze_note', nome: 'Note sulle gravidanze' },
    climaterio: { campo: 'climaterio_note', nome: 'Note sul climaterio' },
    menopausa: { campo: 'menopausa_note', nome: 'Note sulla menopausa' },
};

/* NELLA SCHEDA GINECOLOGICA il riquadro va dopo la sezione della storia
   ostetrica, riconosciuta dal campo «Gravidanze» (GIN_gravidanze) e non dal
   titolo, che dalla schermata «Schede cliniche» si puo' rinominare. Se quel
   campo non c'e' - una scheda rifatta senza - il riquadro va in fondo: non
   deve sparire, perche' dalla scheda sono usciti «In menopausa», «Età della
   menopausa» e «Parti» e questo riquadro li sostituisce. Scritto qui una
   volta sola perche' la scheda si disegna in due posti (profilo e
   procedura). Restituisce l'id della sezione, 'fondo', o null per ogni
   altra scheda. */
export const SCHEDA_GINECOLOGICA = 'scheda_ginecologia';
export function dopoQualeSezione(codiceScheda, sezioni) {
    if (codiceScheda !== SCHEDA_GINECOLOGICA) return null;
    const sez = (sezioni || [])
        .filter((z) => z.campi.length > 0)
        .find((z) => z.campi.some((c) => c.codice === 'GIN_gravidanze'));
    return sez ? sez.id : 'fondo';
}

/* Le tre forme ammesse per la data del parto, come per gli interventi:
   chi ricorda solo l'anno non deve inventare un giorno. */
const DATA_PARZIALE = /^\d{4}(-\d{2}(-\d{2})?)?$/;

const vuota = () => ({ data_parto: '', fisiologica: null, altro: '' });

/* I campi di questo riquadro che si scrivono: lo stato si calcola da loro. */
const CAMPI_SCRITTI = ['gravidanze', 'gravidanze_elenco', 'climaterio', 'climaterio_sintomi', 'menopausa', 'menopausa_eta',
    'gravidanze_note', 'climaterio_note', 'menopausa_note'];
const scritta = (x) => !!(String(x.data_parto || '').trim() || x.fisiologica !== null || String(x.altro || '').trim());

const dalServer = (elenco) => {
    const righe = Array.isArray(elenco) ? elenco : [];
    return righe.length
        ? righe.map((x) => ({
            data_parto: x?.data_parto || '',
            fisiologica: x?.fisiologica === true ? true : (x?.fisiologica === false ? false : null),
            altro: x?.altro || '',
        }))
        : [vuota()];
};

/** «23/09/2026 12:40», ora italiana. */
const quando = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d)) return '';
    return d.toLocaleString('it-IT', {
        timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

/** «2026-03-15» -> «15/03/2026». */
const dataItaliana = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/**
 * @param {number}   pazienteId
 * @param {string}   origine          'anamnesi' | 'scheda_ginecologica'
 * @param {number}   compilazioneId   dentro una scheda: la compilazione aperta
 * @param {number}   visitaId         dentro una procedura: la visita (se la scheda non e' ancora salvata)
 * @param {function} onApriAnamnesi   il collegamento «apri l'anamnesi», dove c'e'
 */
const RiquadroGravidanze = ({ pazienteId, origine = 'anamnesi', compilazioneId = null, visitaId = null,
                              onApriAnamnesi = null }) => {
    const [dati, setDati] = useState(null);
    const [sesso, setSesso] = useState(null);
    const [gravidanze, setGravidanze] = useState([vuota()]);
    const [etaSbagliata, setEtaSbagliata] = useState(false);
    const [versione, setVersione] = useState(0);
    const [errore, setErrore] = useState(null);
    const [solaLettura, setSolaLettura] = useState(false);
    const [valoreAl, setValoreAl] = useState(null);
    const solaLetturaOra = useRef(false);
    /* Il motivo di un rifiuto del server per una nota (per esempio oltre
       2000 caratteri): si dice sotto il campo, col testo del server, e il
       testo resta nel campo. Gli altri campi del riquadro hanno i loro
       messaggi scritti qui. */
    const [rifiutoNota, setRifiutoNota] = useState({});

    /* Il salvataggio di un campo, col posto da cui si scrive. L'ultima
       modifica la scrive il database: si prende dalla risposta. */
    const scrivi = useCallback(async (campo, valore) => {
        const nota = Object.values(NOTA_DI).some((x) => x.campo === campo);
        let r;
        try {
            r = await anamnesiFisiologicaAPI.saveDettaglio(pazienteId, { [campo]: valore }, { origine });
        } catch (e) {
            if (nota) setRifiutoNota((p) => ({ ...p, [campo]: messaggioDa(e, 'il salvataggio non è riuscito') }));
            throw e;
        }
        if (nota) setRifiutoNota((p) => ({ ...p, [campo]: null }));
        const d = r.data?.data?.dettaglio;
        if (d) {
            setDati((p) => ({
                ...(p || {}),
                gravidanze_aggiornato_il: d.gravidanze_aggiornato_il,
                gravidanze_aggiornato_nome: d.gravidanze_aggiornato_nome,
                gravidanze_aggiornato_dove: d.gravidanze_aggiornato_dove,
            }));
        }
        return r;
    }, [pazienteId, origine]);
    const { stato: statoCampi, salva: salvaVero, nonSalvati, salvatoAlle, segnaBase, uscita } = useSalvaCampo(
        scrivi, useCallback(() => setVersione((v) => v + 1), []),
        /* Il riquadro si smonta senza che il campo perda il fuoco (cambio di
           linguetta, Ctrl K): quello che e' scritto e non uscito si salva
           comunque, se differisce dall'archivio. */
        { allaChiusura: () => salvaAperti(), bloccato: () => dateParto.length > 0 || etaSbagliata });
    /* IN SOLA LETTURA NON PARTE NIENTE. Un campo in sola lettura perde il
       fuoco come gli altri: senza questa guardia l'uscita dal campo
       riscriverebbe nel dato di adesso il valore di un giorno passato.
       Tutti i salvataggi del riquadro passano da qui. */
    const salvaCampo = useCallback((campo, valore) => {
        if (solaLetturaOra.current) return;
        salvaVero(campo, valore);
    }, [salvaVero]);

    /* Dentro una scheda il valore e' quello del giorno della visita, e se
       e' modificabile lo dice il server (la data italiana, non il fuso del
       browser). Nell'anamnesi e' sempre il dato di adesso. */
    const delGiorno = origine === 'scheda_ginecologica' && !!(compilazioneId || visitaId);
    const carica = useCallback(async () => {
        try {
            const r = delGiorno
                ? await anamnesiFisiologicaAPI.getGravidanzeAl(pazienteId,
                    compilazioneId ? { compilazione: compilazioneId } : { visita: visitaId })
                : await anamnesiFisiologicaAPI.getDettaglio(pazienteId);
            const x = r.data?.data || {};
            const d = x.dettaglio || {};
            solaLetturaOra.current = delGiorno && x.modificabile === false;
            setSolaLettura(solaLetturaOra.current);
            setValoreAl(x.valore_al || null);
            setSesso(x.sesso || null);
            setDati(d);
            const righe = dalServer(d.gravidanze_elenco);
            setGravidanze(righe);
            /* Cio' che c'e' in archivio: un campo che esce com'e' non si
               scrive e non dice «salvato». */
            ['gravidanze', 'climaterio', 'menopausa'].forEach((c) => segnaBase(c, d[c] ?? null));
            segnaBase('climaterio_sintomi', d.climaterio_sintomi || null);
            segnaBase('menopausa_eta', d.menopausa_eta ?? null);
            Object.values(NOTA_DI).forEach(({ campo }) => segnaBase(campo, d[campo] || null));
            segnaBase('gravidanze_elenco', righe.filter(scritta));
            setVersione((v) => v + 1);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Non riesco a leggere gravidanze, climaterio e menopausa');
        }
    }, [pazienteId, delGiorno, compilazioneId, visitaId, segnaBase]);

    /* Si ricarica quando cambia CHE COSA si mostra: un altro paziente,
       un'altra compilazione. Risalvare la scheda no: il giorno della visita
       e' la fine di quel giorno, e un salvataggio dopo non lo sposta. */
    useEffect(() => { carica(); }, [carica]);

    const sporco = useSporco({
        g: dati?.gravidanze, c: dati?.climaterio, cs: dati?.climaterio_sintomi,
        m: dati?.menopausa, me: dati?.menopausa_eta, gravidanze,
        gn: dati?.gravidanze_note, cn: dati?.climaterio_note, mn: dati?.menopausa_note,
    }, versione);
    useUscitaProtetta(sporco || nonSalvati.length > 0, nonSalvati.length || null, uscita);

    const valoreDi = (campo) => {
        if (campo === 'gravidanze_elenco') return gravidanze.filter(scritta);
        if (campo === 'climaterio_sintomi') return dati?.climaterio_sintomi || null;
        if (Object.values(NOTA_DI).some((n) => n.campo === campo)) return dati?.[campo] || null;
        return dati?.[campo] ?? null;
    };

    const segno = (campo) => {
        const st = statoCampi[campo];
        if (!st) return null;
        if (st === 'in_corso') return <span className="sf-segno sf-segno--corso" title="Sto salvando…">…</span>;
        if (st === 'salvato') return <span className="sf-segno sf-segno--ok" title="Salvato"><Check size={13} aria-hidden="true" /></span>;
        return (
            <button type="button" className="sf-segno sf-segno--no"
                    title="Non salvato: clicca per riprovare"
                    onClick={() => salvaCampo(campo, valoreDi(campo))}>non salvato</button>
        );
    };
    const classeCampo = (campo) => (statoCampi[campo] === 'non_salvato' ? 'sf-testo--rosso' : '');

    const cambia = (campo, valore) => setDati((p) => ({ ...(p || {}), [campo]: valore }));

    const dateParto = gravidanze
        .map((x, i) => ({ i, ok: !x.data_parto || DATA_PARZIALE.test(String(x.data_parto).trim()) }))
        .filter((x) => !x.ok)
        .map((x) => x.i);

    /* L'elenco e' una colonna sola (jsonb): si salva intero. Con una data
       scritta male non parte: il server la rifiuterebbe, e il perche' si
       legge sotto l'elenco. */
    const salvaGravidanze = (lista = gravidanze) => {
        if (lista.some((x) => x.data_parto && !DATA_PARZIALE.test(String(x.data_parto).trim()))) return;
        /* Solo le righe che hanno qualcosa: il server toglie le altre, e
           aggiungere una riga e lasciarla bianca non e' una modifica. */
        salvaCampo('gravidanze_elenco', lista.filter(scritta));
    };

    const cambiaGravidanza = (i, campo, v) => {
        setGravidanze((p) => p.map((x, k) => (k === i ? { ...x, [campo]: v } : x)));
    };

    /* Una spunta non ha un'uscita dal campo: l'elenco si salva al clic. */
    const scegliFisiologica = (i, valore) => {
        const lista = gravidanze.map((x, k) => (k === i
            ? { ...x, fisiologica: x.fisiologica === valore ? null : valore } : x));
        setGravidanze(lista);
        salvaGravidanze(lista);
    };

    const aggiungiGravidanza = () => setGravidanze((p) => [...p, vuota()]);

    const togliGravidanza = (i) => {
        if (scritta(gravidanze[i])
            && !window.confirm('Togli questa gravidanza? Quello che c\'era resta nel registro delle modifiche.')) return;
        const lista = gravidanze.length === 1 ? [vuota()] : gravidanze.filter((_, k) => k !== i);
        setGravidanze(lista);
        salvaGravidanze(lista);
    };

    /* Tutto cio' che e' scritto e puo' non essere ancora uscito: per lo
       smontaggio. Scrive solo cio' che differisce dall'archivio. */
    const salvaAperti = () => {
        if (!dati || sesso !== 'F') return true;
        salvaGravidanze();
        Object.values(NOTA_DI).forEach(({ campo }) => salvaCampo(campo, dati[campo] || null));
        if (dati.climaterio === true) salvaCampo('climaterio_sintomi', dati.climaterio_sintomi || null);
        let puoPartire = dateParto.length === 0;
        if (dati.menopausa === true) {
            const t = String(dati.menopausa_eta ?? '').trim();
            const n = Number(t);
            if (!t) salvaCampo('menopausa_eta', null);
            else if (Number.isInteger(n) && n >= 10 && n <= 70) salvaCampo('menopausa_eta', n);
            else puoPartire = false;
        }
        return puoPartire;
    };

    /* Lo stato del riquadro, e il suo riprova: i campi rossi si riscrivono
       uno per uno. Con una data del parto scritta male il riquadro e'
       «bloccato»: riprovare rifa' il controllo. */
    const riprova = () => {
        nonSalvati.filter((c) => c !== 'gravidanze_elenco').forEach((c) => salvaCampo(c, valoreDi(c)));
        salvaGravidanze();
    };

    /* Si/no. Ripremere la scelta gia' fatta la toglie, come per il parto.
       Senza il si il campo che ne dipende si chiude: se c'era scritto
       qualcosa si chiede prima, poi si cancella nello stesso salvataggio. */
    const scegliSiNo = (campo, valore) => {
        const nuovo = dati?.[campo] === valore ? null : valore;
        if (nuovo !== true) {
            const dipendente = DIPENDE_DA[campo];
            const pieno = campo === 'gravidanze'
                ? gravidanze.some(scritta)
                : (dati?.[dipendente] !== null && dati?.[dipendente] !== undefined && dati?.[dipendente] !== '');
            if (pieno && !window.confirm(CONFERMA[campo])) return;
            if (campo === 'gravidanze') setGravidanze([vuota()]);
            else cambia(dipendente, null);
            /* Il server svuota il campo che dipende dal «no»: in archivio non
               c'e' piu' quello di prima. */
            segnaBase(dipendente, campo === 'gravidanze' ? [] : null);
            if (campo === 'menopausa') setEtaSbagliata(false);
        }
        cambia(campo, nuovo);
        salvaCampo(campo, nuovo);
    };

    /* L'eta': 10-70 sono limiti di plausibilita', per fermare un errore di
       battitura, non valori clinici. Fuori da li' non parte. */
    const salvaEta = (testo) => {
        const t = String(testo ?? '').trim();
        if (!t) { setEtaSbagliata(false); salvaCampo('menopausa_eta', null); return; }
        const n = Number(t);
        if (!Number.isInteger(n) || n < 10 || n > 70) { setEtaSbagliata(true); return; }
        setEtaSbagliata(false);
        salvaCampo('menopausa_eta', n);
    };

    /* Solo per le pazienti: il sesso lo dice la rotta, anche dove - nella
       scheda ginecologica - nessuno lo passa. «Parto» resta per tutti. */
    if (!dati || sesso !== 'F') return errore ? <p className="sf-errore-riga">{errore}</p> : null;

    /* I PARTI: le righe con la data del parto. Una riga e' una gravidanza;
       senza data puo' essere in corso, interrotta, o non ricordata, e non
       si conta come parto. Regola da validare con la ginecologa (in
       docs/campi/ginecologia.md). */
    const parti = gravidanze.filter((x) => String(x.data_parto || '').trim()).length;

    const rigaSiNo = (campo, titolo) => (
        <div>
            <span className="sf-etichetta">{titolo}</span>
            <div className="sf-spunte sf-spunte-riga">
                {SI_NO.map((o) => (
                    <label key={String(o.valore)} className="sf-spunta">
                        <input type="checkbox"
                            checked={dati?.[campo] === o.valore}
                            disabled={solaLettura}
                            onChange={() => scegliSiNo(campo, o.valore)} />
                        <span>{o.etichetta}</span>
                    </label>
                ))}
                {segno(campo)}
            </div>
        </div>
    );

    /* La nota di una voce, sotto i suoi Si'/No. In sola lettura si legge solo
       se c'era. */
    const rigaNota = (voce) => {
        const { campo, nome } = NOTA_DI[voce];
        if (solaLettura && !dati?.[campo]) return null;
        return (
            <div className="sf-nota">
                <span className="sf-etichetta">Note</span>
                <textarea className={`sf-testo sf-testo--pieno sf-nota-campo ${classeCampo(campo)}`}
                    rows={2}
                    aria-label={nome}
                    placeholder="facoltativa"
                    readOnly={solaLettura}
                    value={dati?.[campo] || ''}
                    onChange={(e) => cambia(campo, e.target.value)}
                    onBlur={(e) => salvaCampo(campo, e.target.value || null)}
                    onKeyDown={conInvio((e) => salvaCampo(campo, e.target.value || null))} />
                {segno(campo)}
                {rifiutoNota[campo] && statoCampi[campo] === 'non_salvato' && (
                    <span className="sf-errore-riga" role="alert">
                        Non salvato: {rifiutoNota[campo]}. Il testo è ancora nel campo.
                    </span>
                )}
            </div>
        );
    };

    return (
        <section data-salva-da-solo className={`sf-riquadro ${origine === 'scheda_ginecologica' ? 'sf-riquadro--in-scheda' : ''} ${solaLettura ? 'sf-riquadro--sola-lettura' : ''}`}>
            <h3><Flower2 size={16} strokeWidth={1.8} aria-hidden="true" /> Gravidanze, climaterio e menopausa</h3>
            {/* Chi l'ha scritto, quando e da dove: e' la prima cosa da sapere
                di un dato che si scrive da due posti. */}
            {dati.gravidanze_aggiornato_il && (
                <span className="sf-ultima-modifica">
                    Ultima modifica: <b>{dati.gravidanze_aggiornato_nome || 'utente non indicato'}</b>,
                    {' '}{quando(dati.gravidanze_aggiornato_il)}
                    {DOVE[dati.gravidanze_aggiornato_dove] ? `, ${DOVE[dati.gravidanze_aggiornato_dove]}` : ''}
                </span>
            )}
            {solaLettura ? (
                /* Un giorno passato: com'era quel giorno. Si modifica solo il
                   dato di adesso, e il dato di adesso sta nell'anamnesi. */
                <span className="sf-valore-al">
                    Valore al {dataItaliana(valoreAl)} — per modificarlo{' '}
                    {onApriAnamnesi
                        ? <button type="button" className="sf-link" onClick={onApriAnamnesi}>apri l&apos;anamnesi</button>
                        : 'apri l\'anamnesi'}
                </span>
            ) : (
                <StatoSalvataggio campi={CAMPI_SCRITTI} stato={statoCampi} salvatoAlle={salvatoAlle}
                    bloccato={dateParto.length > 0 || etaSbagliata} onRiprova={riprova}
                    frase={origine === 'scheda_ginecologica'
                        ? 'È il dato dell\'anamnesi, lo stesso del riquadro sotto «Parto», scritto da due posti. Si salva appena esci dal campo, non con «Salva la scheda».'
                        : 'Lo stesso riquadro compare nella scheda ginecologica: è lo stesso dato, scritto da due posti. Si salva appena esci dal campo.'} />
            )}
            {solaLettura && [dati.gravidanze, dati.climaterio, dati.menopausa,
                dati.gravidanze_note, dati.climaterio_note, dati.menopausa_note].every((v) => v === null || v === undefined) && (
                <span className="sf-aiuto">A quella data non era registrato niente.</span>
            )}

            <div className="sf-riga-scelta">
                {rigaSiNo('gravidanze', 'Gravidanze')}
                {dati?.gravidanze === true && !solaLettura && (
                    <span className="sf-aiuto">
                        Una riga per ogni gravidanza. La data del parto può essere incompleta:
                        <b> 2019</b>, <b>2019-03</b> oppure <b>2019-03-14</b>.
                    </span>
                )}
            </div>
            {dati?.gravidanze === true && (
                <div className="sf-interventi">
                    {gravidanze.map((x, i) => (
                        <div key={i} className="sf-intervento sf-gravidanza">
                            <input
                                className={`sf-testo sf-data ${dateParto.includes(i) ? 'errore' : ''}`}
                                placeholder="Data del parto"
                                readOnly={solaLettura}
                                value={x.data_parto}
                                onChange={(e) => cambiaGravidanza(i, 'data_parto', e.target.value)}
                                onBlur={() => salvaGravidanze()}
                                onKeyDown={conInvio(() => salvaGravidanze())} />
                            <div className="sf-spunte sf-spunte-riga">
                                <span className="sf-aiuto">Fisiologica</span>
                                {SI_NO.map((o) => (
                                    <label key={String(o.valore)} className="sf-spunta">
                                        <input type="checkbox"
                                            checked={x.fisiologica === o.valore}
                                            disabled={solaLettura}
                                            onChange={() => scegliFisiologica(i, o.valore)} />
                                        <span>{o.etichetta}</span>
                                    </label>
                                ))}
                            </div>
                            <input className="sf-testo"
                                placeholder="Altro"
                                readOnly={solaLettura}
                                value={x.altro}
                                onChange={(e) => cambiaGravidanza(i, 'altro', e.target.value)}
                                onBlur={() => salvaGravidanze()}
                                onKeyDown={conInvio(() => salvaGravidanze())} />
                            {!solaLettura && (
                                <button type="button" className="sf-toglie"
                                    onClick={() => togliGravidanza(i)}
                                    title="Togli questa gravidanza">
                                    <Trash2 size={15} aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    ))}
                    {dateParto.length > 0 && (
                        <span className="sf-errore-riga">
                            La data del parto va scritta come 2019, 2019-03 oppure 2019-03-14.
                        </span>
                    )}
                    {!solaLettura && (
                        <div>
                            <button type="button" className="sf-btn" onClick={aggiungiGravidanza}>
                                <Plus size={15} aria-hidden="true" /> Aggiungi una gravidanza
                            </button>
                            {segno('gravidanze_elenco')}
                        </div>
                    )}
                    {/* Il numero dei parti non si scrive: si conta dalle
                        righe, cosi' non puo' dire una cosa diversa
                        dall'elenco. */}
                    <span className="sf-etichetta sf-parti">
                        Parti: {parti}{' '}
                        <span className="sf-aiuto">contati dalle righe con la data del parto: non si scrivono</span>
                    </span>
                </div>
            )}
            {/* La nota di ogni voce sta in fondo alla voce, larga quanto il
                riquadro: col «Sì» sotto le righe che la voce apre. */}
            {rigaNota('gravidanze')}

            <div className="sf-riga-scelta">
                {rigaSiNo('climaterio', 'Climaterio')}
                {/* L'etichetta sta sopra il campo: il testo segnaposto
                    sparisce appena si scrive, e «vampate» da solo non dice
                    che cosa sia. */}
                {dati?.climaterio === true && (
                    <div>
                        <span className="sf-etichetta">Sintomi</span>
                        <input className={`sf-testo sf-testo--pieno ${classeCampo('climaterio_sintomi')}`}
                            placeholder="Quelli che riferisce la paziente"
                            readOnly={solaLettura}
                            value={dati?.climaterio_sintomi || ''}
                            onChange={(e) => cambia('climaterio_sintomi', e.target.value)}
                            onBlur={(e) => salvaCampo('climaterio_sintomi', e.target.value || null)}
                            onKeyDown={conInvio((e) => salvaCampo('climaterio_sintomi', e.target.value || null))} />
                        {segno('climaterio_sintomi')}
                    </div>
                )}
                {rigaNota('climaterio')}
            </div>

            <div className="sf-riga-scelta">
                {rigaSiNo('menopausa', 'Menopausa')}
                {dati?.menopausa === true && (
                    <div>
                        <span className="sf-etichetta">Età menopausa (anni)</span>
                        <input type="number" min="10" max="70" step="1"
                            className={`sf-testo sf-data ${etaSbagliata ? 'errore' : classeCampo('menopausa_eta')}`}
                            placeholder="Età della menopausa (anni)"
                            readOnly={solaLettura}
                            value={dati?.menopausa_eta ?? ''}
                            onChange={(e) => cambia('menopausa_eta', e.target.value)}
                            onBlur={(e) => salvaEta(e.target.value)}
                            onKeyDown={conInvio((e) => salvaEta(e.target.value))} />
                        {segno('menopausa_eta')}
                    </div>
                )}
                {rigaNota('menopausa')}
            </div>
            {etaSbagliata && (
                <span className="sf-errore-riga">
                    L&apos;età della menopausa va da 10 a 70 anni: è un limite di plausibilità,
                    serve a fermare un errore di battitura.
                </span>
            )}
        </section>
    );
};

export default RiquadroGravidanze;
